// designOnlineApi.js
/**
 * API service for Design Online endpoints
 * Simplified version matching old architecture behavior
 */

// Use full API domain (external server)
const ENDPOINTS_BASE = 'https://dev.api-customapp.comda.com/apps/studio';

class DesignOnlineApiService {
  /**
   * Check if product supports Design Online
   * Supports two-phase loading:
   * - Phase 1 (fast): design_enabled=1, force_probe=0 (metafield short-circuit)
   * - Phase 2 (lazy): design_enabled=1, force_probe=1 (fetch URLs from Studio API)
   * 
   * @param {string} productId - Product ID from Shopify
   * @param {string} sku - Product SKU
   * @param {object} options - Optional parameters
   * @param {boolean} options.design_enabled - Product has design_enabled metafield
   * @param {boolean} options.force_probe - Force call to Studio API even with metafield
   */
  async canDesign(productId, sku, options = {}) {
    try {
      const finalProductId = productId;
      
      // Start building URL
      let url = `${ENDPOINTS_BASE}/can-design?product_id=${encodeURIComponent(finalProductId)}`;
      
      if (sku) {
        url += `&sku=${encodeURIComponent(sku)}`;
      }
      
      // Add optional parameters for two-phase loading
      if (options.design_enabled) {
        url += '&design_enabled=1';
      }
      
      if (options.force_probe) {
        url += '&force_probe=1';
      }
      
      // Add lang_id parameter
      url += '&lang_id=EN';

      console.log('[DesignOnlineApi] canDesign request:', { productId: finalProductId, sku, url });

      // Simple fetch - no extra options that might cause issues
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      
      console.log('[DesignOnlineApi] canDesign response:', data);

      return {
        ok: !!data.ok,
        upload_center_url: data.upload_center_url || null,
        customize_studio_url: data.customize_studio_url || null,
        source: data.source || null
      };
    } catch (error) {
      console.error('[DesignOnlineApi] canDesign failed:', error);
      throw error;
    }
  }

  /**
   * Get redirect URL for editing existing design in studio
   * (must return URL that will come back with token_id + ex_response_param)
   */
  async getEditRedirectUrl({ tokenId, returnPage, returnUrl, userId }) {
    const url =
        `${ENDPOINTS_BASE}/redirect-edit` +
        `?token_id=${encodeURIComponent(tokenId)}` +
        `&return_page=${encodeURIComponent(returnPage)}` +
        `&return_url=${encodeURIComponent(returnUrl)}` +
        `&user_id=${encodeURIComponent(userId)}` +
        `&lang_id=EN`;

    console.log('[DesignOnlineApi] getEditRedirectUrl request:', url);

    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    const data = await response.json();
    console.log('[DesignOnlineApi] getEditRedirectUrl response:', data);

    if (data.ok && data.url) return data.url;
    throw new Error('No redirect URL in response');
  }

  /**
   * Fetch available templates for a product from Studio.
   * Returns normalized list: [{ pid, tempid, type, title, preview_url }]
   */
  async getProductTemplates(productId, sku) {
    try {
      let url = `${ENDPOINTS_BASE}/product-templates?product_id=${encodeURIComponent(productId)}&lang_id=EN`;
      if (sku) url += `&sku=${encodeURIComponent(sku)}`;

      console.log('[DesignOnlineApi] 📤 /product-templates REQUEST:', { productId, sku });

      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      console.log('[DesignOnlineApi] ✅ /product-templates RESPONSE:', data);

      if (!data.ok) throw new Error(data.reason || 'Templates request failed');

      return data.templates || [];
    } catch (error) {
      console.error('[DesignOnlineApi] getProductTemplates failed:', error);
      throw error;
    }
  }

  /**
   * Get redirect URL for creating design in studio.
   * Requires pid + tempid resolved from getProductTemplates().
   */
  async getRedirectUrl({ productId, sku, pid, tempid, type, returnPage, returnUrl, userId }) {
    try {
      if (!pid || !tempid) throw new Error('Missing pid/tempid for Studio redirect');

      let url = `${ENDPOINTS_BASE}/redirect` +
        `?pid=${encodeURIComponent(pid)}` +
        `&tempid=${encodeURIComponent(tempid)}` +
        `&type=${encodeURIComponent(type || 'S')}` +
        `&return_page=${encodeURIComponent(returnPage)}` +
        `&return_url=${encodeURIComponent(returnUrl)}` +
        `&user_id=${encodeURIComponent(userId)}`;

      if (productId) url += `&product_id=${encodeURIComponent(productId)}`;
      if (sku)       url += `&sku=${encodeURIComponent(sku)}`;
      url += '&lang_id=EN';

      console.log('[DesignOnlineApi] 📤 /redirect REQUEST:', { pid, tempid, type, productId, url });

      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      console.log('[DesignOnlineApi] ✅ /redirect RESPONSE:', data);

      if (data.ok && data.url) return data.url;
      throw new Error(data.reason || 'No redirect URL in response');
    } catch (error) {
      console.error('[DesignOnlineApi] getRedirectUrl failed:', error);
      throw error;
    }
  }

  /**
   * Get redirect URL for upload center
   */
  async getUploadRedirectUrl({ productId, sku, uploadCenterUrl, returnPage, returnUrl, userId }) {
    try {
      const finalProductId = productId;
      
      let url = `${ENDPOINTS_BASE}/redirect-upload` +
        `?return_page=${encodeURIComponent(returnPage)}` +
        `&return_url=${encodeURIComponent(returnUrl)}` +
        `&user_id=${encodeURIComponent(userId)}` +
        `&upload_center_url=${encodeURIComponent(uploadCenterUrl)}`;

      // Always include product_id
      url += `&product_id=${encodeURIComponent(finalProductId)}`;
      
      if (sku) {
        url += `&sku=${encodeURIComponent(sku)}`;
      }
      
      // Add lang_id parameter
      url += '&lang_id=EN';

      console.log('[DesignOnlineApi] getUploadRedirectUrl request:', url);

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      
      console.log('[DesignOnlineApi] getUploadRedirectUrl response:', data);

      if (data.ok && data.url) {
        return data.url;
      }

      throw new Error('No redirect URL in response');
    } catch (error) {
      console.error('[DesignOnlineApi] getUploadRedirectUrl failed:', error);
      throw error;
    }
  }

  /**
   * Get design template data after return from studio
   */
  async getTemplateData({ tokenId, exResponseParam, returnPage, returnUrl, userId }) {
    try {
      const url = `${ENDPOINTS_BASE}/template` +
        `?token_id=${encodeURIComponent(tokenId)}` +
        `&ex_response_param=${encodeURIComponent(exResponseParam)}` +
        `&user_id=${encodeURIComponent(userId)}` +
        `&return_url=${encodeURIComponent(returnUrl)}` +
        `&return_page=${encodeURIComponent(returnPage)}` +
        `&lang_id=EN`;

      console.log('[DesignOnlineApi] 📤 /template REQUEST:', { 
        url,
        tokenId, 
        exResponseParam: exResponseParam.substring(0, 50) + '...' 
      });

      const response = await fetch(url);

      console.log('[DesignOnlineApi] 📥 /template RESPONSE status:', response.status, response.statusText);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[DesignOnlineApi] ❌ /template ERROR response:', errorText);
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      
      console.log('[DesignOnlineApi] ✅ /template SUCCESS response:', data);
      console.log('[DesignOnlineApi] 🔑 design_url in response:', data.design_url);
      console.log('[DesignOnlineApi] 🖼️ preview_image in response:', data.preview_image);

      return {
        token_id: data.token_id || null,
        preview_image: data.preview_image || null,
        design_url: data.design_url || null,
        pdf_preview_url: data.pdf_preview_url || null,
        print_ready_file_url: data.print_ready_file_url || null
      };
    } catch (error) {
      console.error('[DesignOnlineApi] getTemplateData failed:', error);
      throw error;
    }
  }
}

// Export singleton instance
export default new DesignOnlineApiService();
